import { VariantAttribute } from "@swo/shared";
import { PDPPickerState, VariantPickerVariation } from "./utils";

type InactiveAttributeOnClickReason = {
  type: "disabled";
};

export type AttributeOnClickData = {
  type: "attribute-clicked-button";
  value: VariantAttribute;
  action: () => void;
};

export type AttributeLinkData = {
  type: "attribute-clicked-link";
  value: VariantAttribute;
  link: string;
};

export type AttributeInteractionAction = AttributeOnClickData | AttributeLinkData | InactiveAttributeOnClickReason;

export function attributeToPicker(
  attribute: VariantAttribute,
  onClick: AttributeInteractionAction,
  state: PDPPickerState,
  priceLabel?: string,
): VariantPickerVariation {
  // FIXME:
  const noValueText = "None";

  const common: Pick<VariantPickerVariation, "target" | "state" | "isNew"> =
    onClick.type === "disabled"
      ? {
          target: {
            tag: "disabled",
          },
          state,
          isNew: false,
        }
      : onClick.type === "attribute-clicked-button"
        ? {
            target: {
              tag: "click",
              onClick,
            },
            state,
            isNew: false,
          }
        : {
            target: {
              tag: "link",
              link: onClick.link,
            },
            state,
            isNew: false,
          };

  switch (attribute.type) {
    case "color": {
      if (attribute.value && attribute.value.hexValue) {
        return {
          ...common,
          tag: "color-picker" as const,
          name: attribute.value.label,
          additionalText: priceLabel,
          colorHex: attribute.value.hexValue,
        };
      } else {
        return {
          ...common,
          tag: "plain-picker" as const,
          name: noValueText,
          additionalText: priceLabel,
        };
      }
    }

    case "reticle": {
      if (attribute.value) {
        return {
          ...common,
          tag: "image-picker" as const,
          name: attribute.value.reticleKey,
          additionalText: priceLabel,
          image: attribute.value.image
            ? {
                asset: attribute.value.image?.url,
                name: attribute.value.image.description || attribute.value.image.title || "",
              }
            : undefined,
        };
      } else {
        return {
          ...common,
          tag: "image-picker" as const,
          name: noValueText,
          additionalText: priceLabel,
        };
      }
    }

    case "product variant": {
      return {
        ...common,
        tag: "plain-picker" as const,
        name: attribute.value.name,
        additionalText: priceLabel,
      };
    }

    case "clothing-size": {
      return {
        ...common,
        tag: "cloth-size-picker" as const,
        name: attribute.value.label,
        size: {
          data: attribute.value.data,
          label: attribute.value.label,
        },
      };
    }

    case "price-only": {
      return {
        ...common,
        tag: "price-only-picker",
        additionalText: priceLabel,
        name: attribute.value.label,
      };
    }

    case "plain": {
      if (attribute.value) {
        switch (attribute.value.type) {
          case "text": {
            return {
              ...common,
              tag: "plain-picker" as const,
              name: attribute.value.label,
              additionalText: priceLabel,
            };
          }
          case "enum": {
            return {
              ...common,
              tag: "plain-picker" as const,
              name: attribute.value.data.label,
              additionalText: priceLabel,
            };
          }
          case "number": {
            return {
              ...common,
              tag: "plain-picker" as const,
              name: attribute.value.data.toString(),
              additionalText: priceLabel,
            };
          }
          case "boolean": {
            return {
              ...common,
              tag: "plain-picker" as const,
              name: attribute.value.label,
              additionalText: priceLabel,
            };
          }
          default: {
            throw Error(`Attribute type: "${attribute.value?.type}" not supported`);
          }
        }
      } else {
        return {
          ...common,
          name: noValueText,
          additionalText: priceLabel,
          tag: "plain-picker" as const,
        };
      }
    }

    case "product": {
      throw Error(`Attribute type: "${attribute.type}" not supported`);
    }

    default: {
      const _guard: never = attribute;

      return _guard;
    }
  }
}
